import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AppConfigSection = () => {
  const [appSettings, setAppSettings] = useState({
    theme: 'light',
    currency: 'USD',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    numberFormat: 'US',
    chartType: 'candlestick',
    refreshInterval: '30',
    autoRefresh: true,
    soundEnabled: true,
    animationsEnabled: true,
    compactView: false,
    showAdvancedMetrics: true
  });

  const [isLoading, setIsLoading] = useState(false);

  const themeOptions = [
    { value: 'light', label: 'Light Theme' },
    { value: 'dark', label: 'Dark Theme' },
    { value: 'auto', label: 'Auto (System)' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'JPY', label: 'Japanese Yen (¥)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'zh', label: 'Chinese' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' }
  ];

  const numberFormatOptions = [
    { value: 'US', label: '1,234.56 (US)' },
    { value: 'EU', label: '1.234,56 (EU)' },
    { value: 'IN', label: '1,23,456.78 (Indian)' }
  ];

  const chartTypeOptions = [
    { value: 'candlestick', label: 'Candlestick' },
    { value: 'line', label: 'Line Chart' },
    { value: 'area', label: 'Area Chart' },
    { value: 'bar', label: 'Bar Chart' }
  ];

  const refreshIntervalOptions = [
    { value: '15', label: '15 seconds' },
    { value: '30', label: '30 seconds' },
    { value: '60', label: '1 minute' },
    { value: '300', label: '5 minutes' },
    { value: '600', label: '10 minutes' }
  ];

  const handleSettingChange = (key, value) => {
    setAppSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleToggleSetting = (key) => {
    setAppSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    console.log('App settings saved');
  };

  const handleResetToDefaults = () => {
    setAppSettings({
      theme: 'light',
      currency: 'USD',
      language: 'en',
      dateFormat: 'MM/DD/YYYY',
      numberFormat: 'US',
      chartType: 'candlestick',
      refreshInterval: '30',
      autoRefresh: true,
      soundEnabled: true,
      animationsEnabled: true,
      compactView: false,
      showAdvancedMetrics: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Display Settings */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Monitor" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Display Settings</h2>
            <p className="text-sm text-muted-foreground">Customize the appearance of your application</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Theme"
            options={themeOptions}
            value={appSettings.theme}
            onChange={(value) => handleSettingChange('theme', value)}
            description="Choose your preferred color scheme"
          />
          
          <Select
            label="Language"
            options={languageOptions}
            value={appSettings.language}
            onChange={(value) => handleSettingChange('language', value)}
            description="Select your preferred language"
          />
        </div>

        <div className="mt-6 space-y-4">
          <h3 className="text-sm font-medium text-foreground">Interface Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-foreground">Compact View</h4>
                <p className="text-xs text-muted-foreground">Show more data in less space</p>
              </div>
              <Checkbox
                checked={appSettings.compactView}
                onChange={() => handleToggleSetting('compactView')}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-foreground">Animations</h4>
                <p className="text-xs text-muted-foreground">Enable smooth transitions and effects</p>
              </div>
              <Checkbox
                checked={appSettings.animationsEnabled}
                onChange={() => handleToggleSetting('animationsEnabled')}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-foreground">Sound Effects</h4>
                <p className="text-xs text-muted-foreground">Play sounds for notifications and alerts</p>
              </div>
              <Checkbox
                checked={appSettings.soundEnabled}
                onChange={() => handleToggleSetting('soundEnabled')}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-foreground">Advanced Metrics</h4>
                <p className="text-xs text-muted-foreground">Show detailed financial metrics</p>
              </div>
              <Checkbox
                checked={appSettings.showAdvancedMetrics}
                onChange={() => handleToggleSetting('showAdvancedMetrics')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Data & Formatting */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Globe" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Data & Formatting</h2>
            <p className="text-sm text-muted-foreground">Configure how data is displayed and formatted</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Select
            label="Currency"
            options={currencyOptions}
            value={appSettings.currency}
            onChange={(value) => handleSettingChange('currency', value)}
            description="Default currency for display"
          />
          
          <Select
            label="Date Format"
            options={dateFormatOptions}
            value={appSettings.dateFormat}
            onChange={(value) => handleSettingChange('dateFormat', value)}
            description="How dates are displayed"
          />
          
          <Select
            label="Number Format"
            options={numberFormatOptions}
            value={appSettings.numberFormat}
            onChange={(value) => handleSettingChange('numberFormat', value)}
            description="Number formatting style"
          />
        </div>
      </div>

      {/* Chart Settings */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="BarChart3" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Chart Settings</h2>
            <p className="text-sm text-muted-foreground">Configure default chart preferences</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Default Chart Type"
            options={chartTypeOptions}
            value={appSettings.chartType}
            onChange={(value) => handleSettingChange('chartType', value)}
            description="Default chart style for stock data"
          />
          
          <Select
            label="Data Refresh Interval"
            options={refreshIntervalOptions}
            value={appSettings.refreshInterval}
            onChange={(value) => handleSettingChange('refreshInterval', value)}
            description="How often to update live data"
            disabled={!appSettings.autoRefresh}
          />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div>
              <h4 className="text-sm font-medium text-foreground">Auto Refresh</h4>
              <p className="text-xs text-muted-foreground">Automatically update data at set intervals</p>
            </div>
            <Checkbox
              checked={appSettings.autoRefresh}
              onChange={() => handleToggleSetting('autoRefresh')}
            />
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Database" size={20} className="text-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Data Management</h2>
            <p className="text-sm text-muted-foreground">Manage your application data and preferences</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={() => console.log('Exporting data...')}
          >
            Export Data
          </Button>
          
          <Button
            variant="outline"
            iconName="Upload"
            iconPosition="left"
            onClick={() => console.log('Importing data...')}
          >
            Import Data
          </Button>
          
          <Button
            variant="outline"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={handleResetToDefaults}
          >
            Reset to Defaults
          </Button>
          
          <Button
            variant="destructive"
            iconName="Trash2"
            iconPosition="left"
            onClick={() => console.log('Clearing cache...')}
          >
            Clear Cache
          </Button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-3">
        <Button
          variant="outline"
          onClick={handleResetToDefaults}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Reset All
        </Button>
        <Button
          variant="default"
          onClick={handleSaveSettings}
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default AppConfigSection;